import React, { useState, useMemo } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  Check, X, MagnifyingGlass, CalendarBlank, ChatCircle,
  ClockCounterClockwise, FlowArrow, Tag, User as UserIcon, Eye,
} from '@phosphor-icons/react';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';

// ═══════════════════════════════════════════════════════════════
// Reusable custom-filter rule builder + summary helpers.
// Shared by the board "Custom filters" dropdown and the Project
// Settings → Custom Filters management screen.
//
// rule schema (stored verbatim by the backend):
//   { statuses, priorities, assignee_ids, work_types,
//     created_within_days, created_from, created_to,
//     updated_within_days, updated_from, updated_to,
//     commented_within_days, updated_by_ids, commented_by_ids }
// ═══════════════════════════════════════════════════════════════

export function ruleChipSummary(rules = {}) {
  const parts = [];
  if (rules.statuses?.length) parts.push(`${rules.statuses.length} status`);
  if (rules.priorities?.length) parts.push(`${rules.priorities.length} priority`);
  if (rules.assignee_ids?.length) parts.push(`${rules.assignee_ids.length} assignee`);
  if (rules.work_types?.length) parts.push(`${rules.work_types.length} type`);
  if (rules.tags?.length) parts.push(`${rules.tags.length} tag`);
  if (rules.reporter_ids?.length) parts.push(`${rules.reporter_ids.length} reporter`);
  if (rules.created_within_days) parts.push(`created ≤${rules.created_within_days}d`);
  if (rules.created_from || rules.created_to) parts.push(`created ${rules.created_from || '…'}→${rules.created_to || '…'}`);
  if (rules.updated_within_days) parts.push(`updated ≤${rules.updated_within_days}d`);
  if (rules.updated_from || rules.updated_to) parts.push(`updated ${rules.updated_from || '…'}→${rules.updated_to || '…'}`);
  if (rules.due_within_days || rules.due_from || rules.due_to) parts.push('due');
  if (rules.updated_by_ids?.length) parts.push(`updated by ${rules.updated_by_ids.length}`);
  if (rules.commented_within_days) parts.push(`commented ≤${rules.commented_within_days}d`);
  if (rules.commented_by_ids?.length) parts.push(`comment by ${rules.commented_by_ids.length}`);
  const cfn = rules.custom_fields ? Object.values(rules.custom_fields).filter(isActiveCond).length : 0;
  if (cfn) parts.push(`${cfn} field`);
  return parts.length ? parts.join(' · ') : 'No rules';
}

// A custom-field condition counts as "active" only when it carries a value.
function isActiveCond(c) {
  if (!c || typeof c !== 'object') return false;
  return (Array.isArray(c.in) && c.in.length > 0)
    || (c.contains != null && String(c.contains) !== '')
    || (c.min != null && c.min !== '') || (c.max != null && c.max !== '')
    || !!c.within_days || !!c.from || !!c.to;
}

export function ruleFacetCount(r = {}) {
  let n = 0;
  if (r.statuses?.length) n++;
  if (r.priorities?.length) n++;
  if (r.work_types?.length) n++;
  if (r.assignee_ids?.length) n++;
  if (r.tags?.length) n++;
  if (r.reporter_ids?.length) n++;
  if (r.created_within_days || r.created_from || r.created_to) n++;
  if (r.updated_within_days || r.updated_from || r.updated_to) n++;
  if (r.due_within_days || r.due_from || r.due_to) n++;
  if (r.commented_within_days) n++;
  if (r.updated_by_ids?.length) n++;
  if (r.commented_by_ids?.length) n++;
  if (r.custom_fields) n += Object.values(r.custom_fields).filter(isActiveCond).length;
  return n;
}

const STATUS_COLOR_MAP = {
  todo: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  review: 'bg-amber-50 text-amber-700 border-amber-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const PRIORITY_COLOR_MAP = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  high: 'bg-rose-50 text-rose-700 border-rose-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const TYPE_COLOR_MAP = {
  task: 'bg-blue-50 text-blue-700 border-blue-200',
  story: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  bug: 'bg-red-50 text-red-700 border-red-200',
};

function PillToggle({ label, selected, onClick, color, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11.5px] font-semibold transition-all
        ${selected ? `${color} ring-2 ring-offset-1 ring-blue-300/50 shadow-sm` : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50'}`}
    >
      {selected && <Check size={10} weight="bold" />}
      {label}
    </button>
  );
}

function FilterCard({ icon: Icon, title, description, count, onClear, children }) {
  return (
    <div className="rounded-lg border border-zinc-200 overflow-hidden bg-white">
      <div className="px-3.5 py-2.5 bg-zinc-50/60 border-b border-zinc-100 flex items-center gap-2">
        {Icon && <Icon size={14} className="text-zinc-500 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-bold text-zinc-700">{title}</p>
          {description && <p className="text-[10.5px] text-zinc-400 truncate">{description}</p>}
        </div>
        {count > 0 && (
          <>
            <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold">{count}</span>
            <button type="button" onClick={onClear} className="text-zinc-400 hover:text-zinc-700 p-0.5 rounded hover:bg-zinc-100" title="Clear">
              <X size={11} weight="bold" />
            </button>
          </>
        )}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function MemberPickerRich({ rules, ruleKey, members, onChange, placeholder = 'Search members…' }) {
  const [q, setQ] = useState('');
  const selected = rules[ruleKey] || [];
  const list = useMemo(() => {
    const f = (members || []).filter(m => !q || m.name?.toLowerCase().includes(q.toLowerCase()));
    return f.slice(0, 100);
  }, [members, q]);
  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    onChange({ ...rules, [ruleKey]: Array.from(s) });
  };
  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map(uid => {
            const m = members.find(x => x.user_id === uid);
            const s = getAvatarStyle(uid);
            return (
              <span key={uid} className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-[11px]">
                <Avatar className="h-4 w-4"><AvatarImage src={m?.avatar} /><AvatarFallback className={`${s.bg} text-white text-[7px] font-bold`}>{getInitials(m?.name)}</AvatarFallback></Avatar>
                <span className="text-zinc-700">{m?.name || m?.email || uid}</span>
                <button type="button" onClick={() => toggle(uid)} className="text-zinc-400 hover:text-red-500"><X size={10} /></button>
              </span>
            );
          })}
        </div>
      )}
      <div className="relative">
        <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder} className="h-7 pl-7 text-[12px]" />
      </div>
      <div className="max-h-32 overflow-y-auto rounded border border-zinc-100 bg-white">
        {list.length === 0 && <p className="text-[11px] text-zinc-400 text-center py-3">No members found</p>}
        {list.map(m => {
          const sel = selected.includes(m.user_id);
          const s = getAvatarStyle(m.user_id);
          return (
            <button
              key={m.user_id}
              type="button"
              onClick={() => toggle(m.user_id)}
              data-testid={`rule-member-${ruleKey}-${m.user_id}`}
              className={`w-full flex items-center gap-2 px-2 py-1.5 text-[12px] text-left hover:bg-zinc-50 transition-colors ${sel ? 'bg-blue-50/40' : ''}`}
            >
              <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded border flex-shrink-0 ${sel ? 'bg-blue-600 border-blue-600 text-white' : 'border-zinc-300'}`}>
                {sel && <Check size={9} weight="bold" />}
              </span>
              <Avatar className="h-5 w-5"><AvatarImage src={m.avatar} /><AvatarFallback className={`${s.bg} text-white text-[8px] font-bold`}>{getInitials(m.name)}</AvatarFallback></Avatar>
              <span className="flex-1 truncate text-zinc-700">{m.name || m.email || m.user_id}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const TIME_WINDOWS = [
  { id: null, label: 'Any time' },
  { id: 1, label: 'Today' },
  { id: 2, label: 'Last 2 days' },
  { id: 7, label: 'Last 7 days' },
  { id: 14, label: 'Last 14 days' },
  { id: 30, label: 'Last 30 days' },
  { id: 90, label: 'Last 90 days' },
];

function TimeWindowPills({ value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {TIME_WINDOWS.map(opt => {
        const sel = value === opt.id || (opt.id === null && !value);
        return (
          <button
            key={String(opt.id)}
            type="button"
            onClick={() => onChange(opt.id)}
            data-testid={`${testid}-${opt.id ?? 'any'}`}
            className={`px-2.5 py-1 rounded-full border text-[11.5px] font-semibold transition-all
              ${sel ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50'}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// Absolute From–To date range. `fromKey`/`toKey` are the rule keys mutated.
// Picking a range clears the sibling relative-window key (mutually exclusive)
// so the two date modes never fight each other — a common source of confusion.
function DateRangePicker({ rules, fromKey, toKey, relKey, onChange, testid }) {
  const set = (key, val) => {
    const next = { ...rules, [key]: val || undefined };
    if (val && relKey) delete next[relKey]; // range wins over relative window
    onChange(next);
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1.5">
        <span className="text-[10.5px] font-semibold text-zinc-400 uppercase tracking-wide">From</span>
        <Input
          type="date"
          value={rules[fromKey] || ''}
          max={rules[toKey] || undefined}
          onChange={(e) => set(fromKey, e.target.value)}
          className="h-7 text-[11.5px] w-[140px]"
          data-testid={`${testid}-from`}
        />
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[10.5px] font-semibold text-zinc-400 uppercase tracking-wide">To</span>
        <Input
          type="date"
          value={rules[toKey] || ''}
          min={rules[fromKey] || undefined}
          onChange={(e) => set(toKey, e.target.value)}
          className="h-7 text-[11.5px] w-[140px]"
          data-testid={`${testid}-to`}
        />
      </div>
      {(rules[fromKey] || rules[toKey]) && (
        <button
          type="button"
          onClick={() => { const n = { ...rules }; delete n[fromKey]; delete n[toKey]; onChange(n); }}
          className="text-[11px] text-zinc-400 hover:text-red-500 flex items-center gap-1"
          data-testid={`${testid}-clear`}
        >
          <X size={10} /> Clear range
        </button>
      )}
    </div>
  );
}

// Human-readable, plain-English description of a rule set, e.g.
//   ["Status is To Do or Done", "Priority is High", "Created in the last 7 days"]
// Rendered as a "Filter preview" so users understand exactly what a filter does.
const WINDOW_LABEL = {
  1: 'today', 2: 'the last 2 days', 7: 'the last 7 days',
  14: 'the last 14 days', 30: 'the last 30 days', 90: 'the last 90 days',
};
const cap = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
function rangeText(from, to) {
  if (from && to) return `between ${from} and ${to}`;
  if (from) return `on or after ${from}`;
  return `on or before ${to}`;
}
export function describeRule(rules = {}, { statuses = [], members = [], customFields = [] } = {}) {
  const nameFor = (id) => { const m = members.find(x => x.user_id === id); return m?.name || m?.email || id; };
  const statusLabel = (id) => statuses.find(s => s.id === id)?.label || cap(id);
  const orJoin = (arr, map) => arr.map(map).join(' or ');
  const windowText = (d) => WINDOW_LABEL[d] || `the last ${d} days`;
  const parts = [];
  if (rules.statuses?.length) parts.push(`Status is ${orJoin(rules.statuses, statusLabel)}`);
  if (rules.priorities?.length) parts.push(`Priority is ${orJoin(rules.priorities, cap)}`);
  if (rules.work_types?.length) parts.push(`Type is ${orJoin(rules.work_types, cap)}`);
  if (rules.tags?.length) parts.push(`Tag is ${rules.tags.join(' or ')}`);
  if (rules.assignee_ids?.length) parts.push(`Assignee is ${orJoin(rules.assignee_ids, nameFor)}`);
  if (rules.reporter_ids?.length) parts.push(`Reporter is ${orJoin(rules.reporter_ids, nameFor)}`);
  if (rules.created_within_days) parts.push(`Created in ${windowText(rules.created_within_days)}`);
  else if (rules.created_from || rules.created_to) parts.push(`Created ${rangeText(rules.created_from, rules.created_to)}`);
  if (rules.updated_within_days) parts.push(`Updated in ${windowText(rules.updated_within_days)}`);
  else if (rules.updated_from || rules.updated_to) parts.push(`Updated ${rangeText(rules.updated_from, rules.updated_to)}`);
  if (rules.due_within_days) parts.push(`Due within ${rules.due_within_days} day${rules.due_within_days > 1 ? 's' : ''}`);
  else if (rules.due_from || rules.due_to) parts.push(`Due ${rangeText(rules.due_from, rules.due_to)}`);
  if (rules.updated_by_ids?.length) parts.push(`Updated by ${orJoin(rules.updated_by_ids, nameFor)}`);
  if (rules.commented_within_days) parts.push(`Commented in ${windowText(rules.commented_within_days)}`);
  if (rules.commented_by_ids?.length) parts.push(`Last comment by ${orJoin(rules.commented_by_ids, nameFor)}`);
  if (rules.custom_fields) {
    for (const [fid, cond] of Object.entries(rules.custom_fields)) {
      const f = customFields.find(x => x.field_id === fid);
      if (!f || !isActiveCond(cond)) continue;
      if (Array.isArray(cond.in) && cond.in.length) parts.push(`${f.name} is ${cond.in.join(' or ')}`);
      else if (cond.contains) parts.push(`${f.name} contains "${cond.contains}"`);
      else if ((cond.min != null && cond.min !== '') || (cond.max != null && cond.max !== '')) {
        const bits = [];
        if (cond.min != null && cond.min !== '') bits.push(`≥ ${cond.min}`);
        if (cond.max != null && cond.max !== '') bits.push(`≤ ${cond.max}`);
        parts.push(`${f.name} is ${bits.join(' and ')}`);
      } else if (cond.within_days) parts.push(`${f.name} in ${windowText(cond.within_days)}`);
      else if (cond.from || cond.to) parts.push(`${f.name} ${rangeText(cond.from, cond.to)}`);
    }
  }
  return parts;
}

export function RuleBuilder({ rules, statuses, members, onChange, customFields = [], allTags = [] }) {
  const update = (k, v) => onChange({ ...rules, [k]: v });
  const togglePill = (k, id) => {
    const s = new Set(rules[k] || []);
    if (s.has(id)) s.delete(id); else s.add(id);
    update(k, Array.from(s));
  };
  const cleanRule = (...keys) => {
    const next = { ...rules };
    keys.forEach(k => delete next[k]);
    onChange(next);
  };
  // Mutate a single custom field's condition; drop it entirely when emptied.
  const setCF = (fid, cond) => {
    const cf = { ...(rules.custom_fields || {}) };
    if (!cond || !isActiveCond(cond)) delete cf[fid]; else cf[fid] = cond;
    const next = { ...rules, custom_fields: cf };
    if (Object.keys(cf).length === 0) delete next.custom_fields;
    onChange(next);
  };

  const PRIORITY_OPTS = [
    { id: 'critical', label: 'Critical' }, { id: 'high', label: 'High' },
    { id: 'medium', label: 'Medium' }, { id: 'low', label: 'Low' },
  ];
  const TYPE_OPTS = [
    { id: 'story', label: 'Story' }, { id: 'bug', label: 'Bug' }, { id: 'task', label: 'Task' },
  ];

  const previewParts = describeRule(rules, { statuses, members, customFields });

  return (
    <div className="space-y-4">
      {/* Plain-English preview — updates live as rules change so the user
          always knows exactly what the filter will match. */}
      <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3" data-testid="filter-preview">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-blue-700 mb-1.5 flex items-center gap-1.5">
          <Eye size={13} weight="duotone" /> Filter preview
        </p>
        {previewParts.length === 0 ? (
          <p className="text-[12.5px] text-zinc-500">No rules yet — this filter would match <strong>all tasks</strong>.</p>
        ) : (
          <p className="text-[12.5px] text-zinc-700 leading-relaxed">
            Show tasks where{' '}
            {previewParts.map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="text-blue-500 font-bold"> and </span>}
                <span className="font-semibold text-zinc-800">{p}</span>
              </React.Fragment>
            ))}
          </p>
        )}
      </div>

      <FilterCard
        icon={FlowArrow}
        title="Status"
        description="Which workflow stages to include"
        count={(rules.statuses || []).length}
        onClear={() => cleanRule('statuses')}
      >
        <div className="flex flex-wrap gap-1.5">
          {statuses.map(s => (
            <PillToggle
              key={s.id}
              label={s.label}
              selected={(rules.statuses || []).includes(s.id)}
              onClick={() => togglePill('statuses', s.id)}
              color={STATUS_COLOR_MAP[s.id] || 'bg-zinc-100 text-zinc-700 border-zinc-200'}
              testid={`rule-status-${s.id}`}
            />
          ))}
        </div>
      </FilterCard>

      <div className="grid grid-cols-2 gap-3">
        <FilterCard icon={Tag} title="Priority" count={(rules.priorities || []).length} onClear={() => cleanRule('priorities')}>
          <div className="flex flex-wrap gap-1.5">
            {PRIORITY_OPTS.map(p => (
              <PillToggle key={p.id} label={p.label} selected={(rules.priorities || []).includes(p.id)} onClick={() => togglePill('priorities', p.id)} color={PRIORITY_COLOR_MAP[p.id]} testid={`rule-priority-${p.id}`} />
            ))}
          </div>
        </FilterCard>
        <FilterCard icon={Tag} title="Type" count={(rules.work_types || []).length} onClear={() => cleanRule('work_types')}>
          <div className="flex flex-wrap gap-1.5">
            {TYPE_OPTS.map(t => (
              <PillToggle key={t.id} label={t.label} selected={(rules.work_types || []).includes(t.id)} onClick={() => togglePill('work_types', t.id)} color={TYPE_COLOR_MAP[t.id]} testid={`rule-type-${t.id}`} />
            ))}
          </div>
        </FilterCard>
      </div>

      <FilterCard
        icon={UserIcon}
        title="Assignee"
        description="Tasks assigned to any of the selected members"
        count={(rules.assignee_ids || []).length}
        onClear={() => cleanRule('assignee_ids')}
      >
        <MemberPickerRich rules={rules} ruleKey="assignee_ids" members={members} onChange={onChange} />
      </FilterCard>

      <FilterCard
        icon={CalendarBlank}
        title="Created date"
        description="Quick window or an exact From–To range (workspace timezone)"
        count={(rules.created_within_days || rules.created_from || rules.created_to) ? 1 : 0}
        onClear={() => cleanRule('created_within_days', 'created_from', 'created_to')}
      >
        <TimeWindowPills value={rules.created_within_days || null} onChange={(v) => { const n = { ...rules, created_within_days: v || undefined }; if (v) { delete n.created_from; delete n.created_to; } onChange(n); }} testid="rule-window-created" />
        <div className="mt-3 pt-3 border-t border-zinc-100">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-zinc-400 mb-2">Or exact range</p>
          <DateRangePicker rules={rules} fromKey="created_from" toKey="created_to" relKey="created_within_days" onChange={onChange} testid="rule-range-created" />
        </div>
      </FilterCard>

      <FilterCard
        icon={ClockCounterClockwise}
        title="Updated within"
        description="Last activity (status, assignment, fields, comments)"
        count={(rules.updated_within_days || rules.updated_from || rules.updated_to) ? 1 : 0}
        onClear={() => cleanRule('updated_within_days', 'updated_from', 'updated_to')}
      >
        <TimeWindowPills value={rules.updated_within_days || null} onChange={(v) => { const n = { ...rules, updated_within_days: v || undefined }; if (v) { delete n.updated_from; delete n.updated_to; } onChange(n); }} testid="rule-window-updated" />
        <div className="mt-3 pt-3 border-t border-zinc-100">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-zinc-400 mb-2">Or exact range</p>
          <DateRangePicker rules={rules} fromKey="updated_from" toKey="updated_to" relKey="updated_within_days" onChange={onChange} testid="rule-range-updated" />
        </div>
        <div className="mt-3 pt-3 border-t border-zinc-100">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-zinc-400 mb-2">Updated by · {(rules.updated_by_ids || []).length} selected</p>
          <MemberPickerRich rules={rules} ruleKey="updated_by_ids" members={members} onChange={onChange} placeholder="Filter by who updated…" />
        </div>
      </FilterCard>

      <FilterCard
        icon={ChatCircle}
        title="Commented within"
        description="Tasks with at least one comment in the time window"
        count={rules.commented_within_days ? 1 : 0}
        onClear={() => cleanRule('commented_within_days')}
      >
        <TimeWindowPills value={rules.commented_within_days || null} onChange={(v) => update('commented_within_days', v)} testid="rule-window-commented" />
        <div className="mt-3 pt-3 border-t border-zinc-100">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-zinc-400 mb-2">Last comment by · {(rules.commented_by_ids || []).length} selected</p>
          <MemberPickerRich rules={rules} ruleKey="commented_by_ids" members={members} onChange={onChange} placeholder="Filter by who commented…" />
        </div>
      </FilterCard>

      {/* Tags */}
      <FilterCard
        icon={Tag}
        title="Tags"
        description="Match tasks carrying any of the selected tags"
        count={(rules.tags || []).length}
        onClear={() => cleanRule('tags')}
      >
        <TagPicker selected={rules.tags || []} allTags={allTags} onChange={(tags) => update('tags', tags)} />
      </FilterCard>

      {/* Reporter */}
      <FilterCard
        icon={UserIcon}
        title="Reporter"
        description="Tasks created/reported by any of the selected members"
        count={(rules.reporter_ids || []).length}
        onClear={() => cleanRule('reporter_ids')}
      >
        <MemberPickerRich rules={rules} ruleKey="reporter_ids" members={members} onChange={onChange} placeholder="Filter by reporter…" />
      </FilterCard>

      {/* Due date */}
      <FilterCard
        icon={CalendarBlank}
        title="Due date"
        description="Quick window (± from today) or an exact From–To range"
        count={(rules.due_within_days || rules.due_from || rules.due_to) ? 1 : 0}
        onClear={() => cleanRule('due_within_days', 'due_from', 'due_to')}
      >
        <TimeWindowPills value={rules.due_within_days || null} onChange={(v) => { const n = { ...rules, due_within_days: v || undefined }; if (v) { delete n.due_from; delete n.due_to; } onChange(n); }} testid="rule-window-due" />
        <div className="mt-3 pt-3 border-t border-zinc-100">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-zinc-400 mb-2">Or exact range</p>
          <DateRangePicker rules={rules} fromKey="due_from" toKey="due_to" relKey="due_within_days" onChange={onChange} testid="rule-range-due" />
        </div>
      </FilterCard>

      {/* Custom fields — one card per project field, typed to its data type. */}
      {customFields.map(f => (
        <FilterCard
          key={f.field_id}
          icon={CalendarBlank}
          title={f.name}
          description={`Custom field · ${cap(f.type)}`}
          count={isActiveCond(rules.custom_fields?.[f.field_id]) ? 1 : 0}
          onClear={() => setCF(f.field_id, null)}
        >
          <CustomFieldControl field={f} cond={rules.custom_fields?.[f.field_id] || {}} onChange={(cond) => setCF(f.field_id, cond)} />
        </FilterCard>
      ))}
    </div>
  );
}

// Tag multi-select — toggle existing project tags or type a new one.
function TagPicker({ selected = [], allTags = [], onChange }) {
  const [q, setQ] = useState('');
  const toggle = (tag) => {
    const s = new Set(selected);
    if (s.has(tag)) s.delete(tag); else s.add(tag);
    onChange(Array.from(s));
  };
  const query = q.trim();
  const suggestions = (allTags || []).filter(t => !q || t.toLowerCase().includes(q.toLowerCase())).slice(0, 60);
  const canAdd = query && !allTags.includes(query) && !selected.includes(query);
  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map(tag => (
            <span key={tag} className="inline-flex items-center gap-1.5 pl-2 pr-1.5 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-[11px] text-blue-700">
              {tag}
              <button type="button" onClick={() => toggle(tag)} className="text-blue-400 hover:text-red-500"><X size={10} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Tag size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && canAdd) { e.preventDefault(); toggle(query); setQ(''); } }}
          placeholder="Search or add a tag…"
          className="h-7 pl-7 text-[12px]"
          data-testid="rule-tag-input"
        />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {canAdd && (
          <button type="button" onClick={() => { toggle(query); setQ(''); }} className="px-2.5 py-1 rounded-full border border-dashed border-blue-300 text-blue-600 text-[11.5px] font-semibold hover:bg-blue-50" data-testid="rule-tag-add">
            + Add “{query}”
          </button>
        )}
        {suggestions.map(tag => (
          <button key={tag} type="button" onClick={() => toggle(tag)} data-testid={`rule-tag-${tag}`}
            className={`px-2.5 py-1 rounded-full border text-[11.5px] font-semibold transition-all ${selected.includes(tag) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'}`}>
            {selected.includes(tag) && <Check size={9} weight="bold" className="inline mr-1" />}{tag}
          </button>
        ))}
        {suggestions.length === 0 && !canAdd && <p className="text-[11px] text-zinc-400 py-1">No tags on this project yet.</p>}
      </div>
    </div>
  );
}

// Typed control for a custom field's filter condition.
function CustomFieldControl({ field, cond = {}, onChange }) {
  const type = field.type;
  if (type === 'select') {
    const sel = cond.in || [];
    const toggle = (opt) => {
      const s = new Set(sel);
      if (s.has(opt)) s.delete(opt); else s.add(opt);
      onChange({ in: Array.from(s) });
    };
    return (
      <div className="flex flex-wrap gap-1.5">
        {(field.options || []).length === 0 && <p className="text-[11px] text-zinc-400">No options defined.</p>}
        {(field.options || []).map(opt => (
          <PillToggle key={opt} label={opt} selected={sel.includes(opt)} onClick={() => toggle(opt)} color="bg-blue-50 text-blue-700 border-blue-200" testid={`rule-cf-${field.field_id}-${opt}`} />
        ))}
      </div>
    );
  }
  if (type === 'number') {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10.5px] font-semibold text-zinc-400 uppercase">Min</span>
        <Input type="number" value={cond.min ?? ''} onChange={(e) => onChange({ ...cond, min: e.target.value })} className="h-7 w-24 text-[11.5px]" data-testid={`rule-cf-${field.field_id}-min`} />
        <span className="text-[10.5px] font-semibold text-zinc-400 uppercase">Max</span>
        <Input type="number" value={cond.max ?? ''} onChange={(e) => onChange({ ...cond, max: e.target.value })} className="h-7 w-24 text-[11.5px]" data-testid={`rule-cf-${field.field_id}-max`} />
      </div>
    );
  }
  if (type === 'date') {
    return (
      <div className="space-y-2">
        <TimeWindowPills value={cond.within_days || null} onChange={(v) => { const n = { ...cond, within_days: v || undefined }; if (v) { delete n.from; delete n.to; } onChange(n); }} testid={`rule-cf-${field.field_id}-window`} />
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10.5px] font-semibold text-zinc-400 uppercase">From</span>
          <Input type="date" value={cond.from || ''} onChange={(e) => { const n = { ...cond, from: e.target.value || undefined }; if (e.target.value) delete n.within_days; onChange(n); }} className="h-7 w-[140px] text-[11.5px]" data-testid={`rule-cf-${field.field_id}-from`} />
          <span className="text-[10.5px] font-semibold text-zinc-400 uppercase">To</span>
          <Input type="date" value={cond.to || ''} onChange={(e) => { const n = { ...cond, to: e.target.value || undefined }; if (e.target.value) delete n.within_days; onChange(n); }} className="h-7 w-[140px] text-[11.5px]" data-testid={`rule-cf-${field.field_id}-to`} />
        </div>
      </div>
    );
  }
  // text / url / richtext
  return (
    <Input
      value={cond.contains || ''}
      onChange={(e) => onChange({ contains: e.target.value })}
      placeholder="Contains text…"
      className="h-7 text-[12px]"
      data-testid={`rule-cf-${field.field_id}-contains`}
    />
  );
}

// Status option list shared by builders — default workflow + custom statuses.
export function projectStatusOptions(project) {
  return [
    { id: 'todo', label: 'To Do' },
    { id: 'in_progress', label: 'In Progress' },
    { id: 'review', label: 'Review' },
    { id: 'done', label: 'Done' },
    ...((project?.custom_statuses || []).map(s => ({ id: s.id, label: s.label }))),
  ];
}
